// src/components/Assets/hooks/useAssetPermissions.js
// Custom hook for Asset Permissions management - Phase 2 implementation

import { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import * as assetsApi from "../assetsApi";

/**
 * Custom hook for managing asset permissions
 * @param {string} viewType - "admin" for tenant view or "farmer" for farm view
 */
export const useAssetPermissions = (viewType = "admin") => {
  const { token, userData } = useContext(UserContext);
  const [t] = useTranslation();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract tenantId from nested userData structure
  const tenantId = userData?.Tenant?.id || userData?.User?.tenantId || userData?.Employee?.tenantId;
  const farmId = userData?.Employee?.farmId;

  /**
   * Fetch permissions based on view type
   */
  const fetchPermissions = useCallback(async (abortSignal) => {
    // Add proper null checks for required IDs
    if (viewType === "admin" && !tenantId) return;
    if (viewType === "farmer" && !farmId) return;

    setLoading(true);
    setError(null);

    try {
      let data = viewType === "admin"
        ? await assetsApi.fetchTenantPendingPermissions(tenantId, token, abortSignal)
        : await assetsApi.fetchFarmPermissions(farmId, token, abortSignal);

      console.log(`Fetched permissions (viewType: ${viewType}):`, data);
      console.log(`Total permissions count: ${data.length}`);
      console.log(`Permission statuses:`, data.map(p => ({ id: p.id, status: p.status })));

      // Sort by request date descending (newest first)
      // Note: Backend returns ALL permissions, filtering happens in UI if needed
      const sorted = data.sort((a, b) =>
        new Date(b.requestDate) - new Date(a.requestDate)
      );

      setPermissions(sorted);
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        toast.error(t("Failed to load permissions"));
        console.error("Error fetching permissions:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, farmId, viewType, token]);

  /**
   * Approve a permission request
   */
  const approvePermission = async (permissionId) => {
    try {
      const response = await assetsApi.updatePermissionStatus(permissionId, "approved", token);

      // Check if backend returned an error in the response body (HTTP 200 but body.status >= 400)
      if (response.status && response.status >= 400) {
        toast.error(t(response.message) || t("Failed to approve permission"));
        console.error("Error in response body:", response);
        return false;
      }

      toast.success(t(response.message) || t("Permission approved"));
      // Refresh without abort signal since we're staying on the same page
      await fetchPermissions();
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || t("Failed to approve permission");
      toast.error(t(errorMessage));
      console.error("Error approving permission:", err);
      return false;
    }
  };

  /**
   * Deny a permission request
   */
  const denyPermission = async (permissionId) => {
    try {
      const response = await assetsApi.updatePermissionStatus(permissionId, "denied", token);

      // Check if backend returned an error in the response body (HTTP 200 but body.status >= 400)
      if (response.status && response.status >= 400) {
        toast.error(t(response.message) || t("Failed to deny permission"));
        console.error("Error in response body:", response);
        return false;
      }

      toast.success(t(response.message) || t("Permission denied"));
      // Refresh without abort signal since we're staying on the same page
      await fetchPermissions();
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || t("Failed to deny permission");
      toast.error(t(errorMessage));
      console.error("Error denying permission:", err);
      return false;
    }
  };

  /**
   * Submit a new permission request (farmer view)
   */
  const requestPermission = async (permissionData) => {
    const requestPayload = {
      ...permissionData,
      tenantId,
      farmId,
      status: "pending"
    };

    try {
      const response = await assetsApi.requestAssetPermission(requestPayload, token);

      // Check if backend returned an error in the response body (HTTP 200 but body.status >= 400)
      if (response.status && response.status >= 400) {
        toast.error(t(response.message) || t("Failed to submit request"));
        console.error("Error in response body:", response);
        return false;
      }

      toast.success(t(response.message) || t("Permission request submitted"));
      // Refresh without abort signal since we're staying on the same page
      await fetchPermissions();
      return true;
    } catch (err) {
      // Handle both axios errors and potential response body errors
      const errorMessage = err.response?.data?.message || err.message || t("Failed to submit request");
      toast.error(t(errorMessage));
      console.error("Error submitting permission request:", err);
      return false;
    }
  };

  // Fetch permissions on mount and when dependencies change
  useEffect(() => {
    const abortController = new AbortController();
    fetchPermissions(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    fetchPermissions,
    approvePermission,
    denyPermission,
    requestPermission
  };
};

export default useAssetPermissions;
